import {
  IMergeRequestDto,
  IMergeRequestItemDto,
  IMergeResultDto,
  StagedEntity
} from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import React from "react";
import BranchingService from "../../../services/branching-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonDetail, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { BranchMerge } from "./branch-merge";
import BranchingStagedChanges from "./branching-staged-changes";
import "./branching-merge-requests.scss";
import "./branching-merge-request-detail.scss";

/** A reject or a cancel run from the list's handlers, on this detail's buttons. */
export type MergeRequestAction = "reject" | "cancel";

const APPROVE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Approving", success: "Approved" };
const REJECT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Rejecting", success: "Rejected" };
const CANCEL_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Cancelling", success: "Cancelled" };

function initialsOf(name?: string): string {
  const parts: string[] = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export interface IBranchingMergeRequestDetailProps {
  request: IMergeRequestItemDto;
  /** True while a reject/cancel action started from the parent list is in flight. */
  actionInFlight: boolean;
  /** Which of this request's actions the parent is running, if any — its button carries `actionStatus`. */
  action: MergeRequestAction | null;
  /** Where that reject/cancel stands (the parent's state machine). */
  actionStatus: KuikaButtonStatus;
  onBack: () => void;
  onReject: (request: IMergeRequestItemDto, comment: string) => void;
  onCancel: (request: IMergeRequestItemDto) => void;
  /** Called after an approve/merge actually lands — parent reloads the list + project and closes the detail. */
  onApproved: () => void;
}

interface IBranchingMergeRequestDetailState {
  loadingChanges: boolean;
  changes: StagedEntity[];
  rejecting: boolean;
  rejectComment: string;
  approving: boolean;
  /** When set, approving surfaced conflicts against the current target — show the SAME resolver as merge. */
  conflictResult: IMergeResultDto | null;
  resolving: boolean;
  /** Where Approve stands. Owned by `SaveStatus`, never set directly. */
  approveStatus: KuikaButtonStatus;
}

/**
 * Review surface for a single merge request (Option B: list → dedicated detail view).
 *
 * Read-only path reuses the exact Changes-tab viewer so the owner sees WHERE the requester changed
 * things. Approving runs the merge fresh against the CURRENT target: a clean merge lands immediately;
 * conflicts open the SAME conflict resolver as the Merge tab (branch-merge.tsx in reviewMode), where the
 * owner resolves against the live state and finalizes through the merge-request resolve endpoint.
 */
class BranchingMergeRequestDetail extends React.Component<
  IBranchingMergeRequestDetailProps,
  IBranchingMergeRequestDetailState
> {
  state: IBranchingMergeRequestDetailState = {
    loadingChanges: false,
    changes: [],
    rejecting: false,
    rejectComment: "",
    approving: false,
    conflictResult: null,
    resolving: false,
    approveStatus: "idle"
  };

  /**
   * Approve's state machine: a clean merge holds its ✓ on Approve before the detail returns to
   * the list, where the request now stands under Decided.
   */
  private readonly approveStatus: SaveStatus = new SaveStatus((approveStatus: KuikaButtonStatus): void => {
    this.setState({ approveStatus });
  });

  componentWillUnmount(): void {
    this.approveStatus.dispose();
  }

  componentDidMount(): void {
    // Only a pending request has a live "what will be merged" diff worth reviewing; a decided one
    // is historical (already applied / discarded), so we show its outcome instead of a live diff.
    if (this.isPending()) void this.loadChanges();
  }

  componentDidUpdate(prevProps: IBranchingMergeRequestDetailProps): void {
    if (prevProps.request.id !== this.props.request.id) {
      this.setState({ rejecting: false, rejectComment: "", changes: [], conflictResult: null });
      if (this.isPending()) void this.loadChanges();
    }
  }

  private isPending(): boolean {
    return this.props.request.status === "Pending";
  }

  private loadChanges = async (): Promise<void> => {
    this.setState({ loadingChanges: true });
    try {
      const res = await BranchingService.GetMergeRequestChanges(this.props.request.id);
      this.setState({ changes: res?.data ?? [], loadingChanges: false });
    } catch {
      this.setState({ loadingChanges: false });
      KMainFunctions.displayErrorNotification("Failed to load merge request changes.");
    }
  };

  // Approve = run the merge fresh against the current target. Clean → lands + approved. Conflicts →
  // open the resolver (leaves the request pending until the owner resolves + finalizes).
  private handleApprove = async (): Promise<void> => {
    const { request } = this.props;
    if (!this.approveStatus.canStart) return;
    this.approveStatus.start();
    this.setState({ approving: true });
    try {
      const res = await BranchingService.ApproveMergeRequest(request.id);
      const result: IMergeResultDto | undefined = res?.data;
      if (result?.hasConflicts) {
        // Not an outcome yet: the resolver opens and the request stays pending until it finishes.
        this.approveStatus.reset();
        this.setState({ conflictResult: result, approving: false });
        return;
      }
      this.setState({ approving: false });
      this.approveStatus.succeed(this.props.onApproved);
    } catch (error: unknown) {
      // ApproveMergeRequest reports its own failure in a toast; the button's error state is this
      // surface's share of the news, so a second notification is not raised here.
      console.error("Error approving merge request:", error);
      this.approveStatus.fail();
      this.setState({ approving: false });
    }
  };

  // Finalize the owner's conflict resolutions through the merge-request resolve endpoint (not the
  // self-service merge). branch-merge.tsx calls this from its reviewMode.onResolve.
  private handleReviewResolve = (mergeData: IMergeRequestDto): Promise<IMergeResultDto | undefined> => {
    return BranchingService.ResolveMergeRequestConflicts(this.props.request.id, mergeData).then((res) => res?.data);
  };

  private handleResolveEnd = (options?: { completed?: boolean }): void => {
    // completed → the resolved merge landed (branch-merge already showed a success toast) → back to list.
    // otherwise → the owner cancelled out of the resolver → return to the read-only review.
    this.setState({ conflictResult: null });
    if (options?.completed) {
      this.props.onApproved();
    }
  };

  private renderViewerBody(): React.ReactNode {
    const { request } = this.props;
    const { loadingChanges, changes } = this.state;

    // Decided requests are historical — show the outcome, not a (misleading, live) diff.
    if (request.status !== "Pending") {
      return this.renderDecidedState();
    }
    if (loadingChanges) {
      return (
        <KuikaSkeletonRegion label="Loading changes" className="branch-mr-detail__skeleton">
          <KuikaSkeletonDetail />
        </KuikaSkeletonRegion>
      );
    }
    if (changes.length === 0) {
      return (
        <div className="branch-mr-detail__state">
          <KuikaSvg name="git-merge" width={26} height={26} />
          <span className="branch-mr-detail__state-title">No file changes</span>
          <span className="branch-mr-detail__state-hint">This request does not change any design entities.</span>
        </div>
      );
    }
    return <BranchingStagedChanges branchId={request.sourceBranchId} itemsOverride={changes} readOnly />;
  }

  private renderDecidedState(): React.ReactNode {
    const { request } = this.props;
    let icon: string = "git-merge";
    let title: string = "";
    let hint: string = "";
    if (request.status === "Approved") {
      icon = "check";
      title = "This request was approved";
      hint = `Its changes were merged into “${request.targetBranchName}”.`;
    } else if (request.status === "Rejected") {
      icon = "circle-x";
      title = "This request was rejected";
      hint = request.reviewComment || "No changes were applied to the target branch.";
    } else {
      icon = "ban";
      title = "This request was cancelled";
      hint = "No changes were applied to the target branch.";
    }
    return (
      <div className="branch-mr-detail__state">
        <KuikaSvg name={icon} width={26} height={26} />
        <span className="branch-mr-detail__state-title">{title}</span>
        <span className="branch-mr-detail__state-hint">{hint}</span>
        {request.reviewedBy?.name && request.status !== "Cancelled" && (
          <span className="branch-mr-detail__state-hint">
            {request.status} by {request.reviewedBy.name}
          </span>
        )}
      </div>
    );
  }

  private renderHeader(inConflictResolution: boolean): React.ReactNode {
    const { request, onBack } = this.props;
    const statusKind: string = request.status.toLowerCase();
    return (
      <div className="branch-mr-detail__header">
        <button
          type="button"
          className="branch-mr-detail__back"
          onClick={inConflictResolution ? () => this.setState({ conflictResult: null }) : onBack}
        >
          <KuikaSvg name="arrow-left" type="arrows" width={16} height={16} />
          {inConflictResolution ? "Back to changes" : "Merge requests"}
        </button>

        <div className="branch-mr-detail__headline">
          <span className="branch-merge-requests__route">
            <strong>{request.sourceBranchName}</strong>
            <KuikaSvg name="arrow-right" type="arrows" width={13} height={13} />
            <strong>{request.targetBranchName}</strong>
          </span>
          <div className="branch-merge-requests__badges">
            <span className="branch-merge-requests__status" data-status={statusKind}>
              {request.status}
            </span>
            {request.isStale && !inConflictResolution && (
              <span
                className="branch-merge-requests__status"
                data-status="stale"
                title="The branches changed since this request was opened"
              >
                Out of date
              </span>
            )}
          </div>
        </div>

        {inConflictResolution ? (
          <div className="branch-mr-detail__title">
            Resolve conflicts against the current “{request.targetBranchName}”
          </div>
        ) : (
          <>
            {request.title && <div className="branch-mr-detail__title">{request.title}</div>}
            {request.description && <div className="branch-merge-requests__desc">{request.description}</div>}
            <div className="branch-merge-requests__meta">
              {request.requestedBy?.name && (
                <>
                  <span className="branch-merge-requests__avatar" aria-hidden="true">
                    {initialsOf(request.requestedBy.name)}
                  </span>
                  <span>
                    <span className="branch-merge-requests__meta-name">{request.requestedBy.name}</span> opened
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  render(): React.ReactNode {
    const { request, actionInFlight, onReject, onCancel } = this.props;
    const { rejecting, rejectComment, approving, conflictResult } = this.state;

    // Conflicts against the current target → the SAME resolver as the Merge tab, in review mode.
    if (conflictResult) {
      return (
        <div className="branch-mr-detail">
          {this.renderHeader(true)}
          <div className="branch-mr-detail__viewer">
            <BranchMerge
              isLoading={this.state.resolving}
              setLoading={(v: boolean) => this.setState({ resolving: v })}
              endMerge={this.handleResolveEnd}
              reviewMode={{
                initialResult: conflictResult,
                sourceBranchId: request.sourceBranchId,
                targetBranchId: request.targetBranchId,
                targetBranchName: request.targetBranchName,
                onResolve: this.handleReviewResolve
              }}
            />
          </div>
        </div>
      );
    }

    const busy: boolean = actionInFlight || approving || this.state.approveStatus === "success";
    // The action this detail's parent is running carries the status on its own button.
    const rejectStatus: KuikaButtonStatus | undefined =
      this.props.action === "reject" ? this.props.actionStatus : undefined;
    const cancelStatus: KuikaButtonStatus | undefined =
      this.props.action === "cancel" ? this.props.actionStatus : undefined;

    return (
      <div className="branch-mr-detail">
        {this.renderHeader(false)}

        <div className="branch-mr-detail__viewer">{this.renderViewerBody()}</div>

        {request.status === "Pending" && (request.canReview || request.canCancel) && (
          <div className="branch-mr-detail__footer">
            {rejecting ? (
              <div className="branch-mr-detail__reject">
                <KuikaInput
                  type="textarea"
                  placeholder="Reason for rejection (optional)"
                  value={rejectComment}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    this.setState({ rejectComment: e.target.value })
                  }
                />
                <div className="branch-mr-detail__reject-actions">
                  <KuikaButton
                    type="secondary"
                    color="neutral"
                    size="md"
                    disabled={busy}
                    onClick={() => this.setState({ rejecting: false, rejectComment: "" })}
                  >
                    Cancel
                  </KuikaButton>
                  <KuikaButton
                    type="primary"
                    color="danger"
                    size="md"
                    status={rejectStatus}
                    statusLabels={REJECT_STATUS_LABELS}
                    disabled={busy && rejectStatus === undefined}
                    onClick={() => onReject(request, rejectComment.trim())}
                  >
                    Reject request
                  </KuikaButton>
                </div>
              </div>
            ) : (
              <div className="branch-mr-detail__actions">
                {request.canCancel && (
                  <KuikaButton
                    type="tertiary"
                    color="neutral"
                    size="md"
                    status={cancelStatus}
                    statusLabels={CANCEL_STATUS_LABELS}
                    disabled={busy && cancelStatus === undefined}
                    onClick={() => onCancel(request)}
                  >
                    Cancel request
                  </KuikaButton>
                )}
                {request.canReview && (
                  <div className="branch-mr-detail__actions-primary">
                    <KuikaButton
                      type="secondary"
                      color="neutral"
                      size="md"
                      disabled={busy}
                      onClick={() => this.setState({ rejecting: true, rejectComment: "" })}
                    >
                      Reject
                    </KuikaButton>
                    <KuikaButton
                      type="primary"
                      size="md"
                      status={this.state.approveStatus}
                      statusLabels={APPROVE_STATUS_LABELS}
                      disabled={busy && this.state.approveStatus === "idle"}
                      onClick={this.handleApprove}
                    >
                      <span className="branch-merge-requests__btn-label">
                        <KuikaSvg name="check" width={14} height={14} />
                        Approve
                      </span>
                    </KuikaButton>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default BranchingMergeRequestDetail;
